import React, {useEffect, useMemo, useRef, useState} from "react";
import Navbar from "@/components/Navbar/Navbar";
import SideMenu from "@/components/MyAccount/SideMenu";
import ApiHttpClient from "@/HttpClient";
import {UsersDetailsModel} from "@/api/Models";
import {toast} from "@/components/ui/use-toast";
import {TableBody, TableCell, TableRow} from "@/components/ui/table";
import {
    canAccessAddUserPage,
    canAccessChangePasswordButton,
    canAccessModifyUserPage
} from "@/components/Auth/AuthValidator";
import {getDecodedTokenDetails} from "@/components/Token/TokenService";
import {debounce} from "lodash";

interface PaginatedUsersResponse {
    items: UsersDetailsModel[];
    totalCount: number;
    totalPages: number;
    currentPage: number;
}

const SearchField: React.FC<{
    value: string;
    onValueChange: (value: string) => void;
    setKeyword: (value: string) => void;
    setIsNewSearch: (value: boolean) => void;
    disabled: boolean;
    placeholder: string;
}> = ({value, onValueChange, setKeyword, setIsNewSearch, disabled, placeholder}) => {
    const debouncedSearch = useMemo(() =>
        debounce((nextValue: string) => {
            if (nextValue.length > 2) {
                setKeyword(nextValue.toLowerCase());
                setIsNewSearch(true);
            } else {
                setKeyword("");
            }
        }, 1000), [setKeyword, setIsNewSearch]);

    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    return (
        <input
            className="block w-full rounded-lg border border-gray-200 p-3 text-gray-600 hover:border-black disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400 disabled:hover:border-gray-200"
            type="search"
            value={value}
            disabled={disabled}
            onChange={(event) => {
                onValueChange(event.target.value);
                debouncedSearch(event.target.value);
            }}
            placeholder={placeholder}
        />
    );
};

function UserManagement() {
    const [userListData, setUserListData] = useState<PaginatedUsersResponse>();
    const [confirmResetPassword, setConfirmResetPassword] = useState(false);
    const [confirmDeactivateUser, setConfirmDeactivateUser] = useState(false);
    const [userToDeactivate, setUserToDeactivate] = useState("");
    const [userIdToDeactivate, setUserIdToDeactivate] = useState<number | null>(null);
    const [isDeactivating, setIsDeactivating] = useState(false);
    const isDeactivatingRef = useRef(false);
    const [usersRefreshKey, setUsersRefreshKey] = useState(0);
    const [isSaving, setIsSaving] = useState(false);
    const [userName, setUserName] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [canAccessChangePassword, setCanAccessChangePassword] = useState(false);
    const [canAccessCreateNewUser, setCanAccessCreateNewUser] = useState(false);
    const [canAccessModifyUser, setCanAccessModifyUser] = useState(false);
    const [inputValue, setInputValue] = useState(currentPage + 1);
    const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);
    const [isNewSearch, setIsNewSearch] = useState(false);
    const [nameInput, setNameInput] = useState("");
    const [workingPointInput, setWorkingPointInput] = useState("");
    const [searchName, setSearchName] = useState("");
    const [searchWorkingPoint, setSearchWorkingPoint] = useState("");
    
    const pageSize = 20;

    useEffect(() => {
        (async () => {
            const tokenDetails = await getDecodedTokenDetails();

            if (tokenDetails?.token) {
                try {
                    await canAccessChangePasswordButton() ? setCanAccessChangePassword(true) : setCanAccessChangePassword(false);
                    await canAccessModifyUserPage() ? setCanAccessModifyUser(true) : setCanAccessModifyUser(false); 
                    await canAccessAddUserPage() ? setCanAccessCreateNewUser(true) : setCanAccessCreateNewUser(false);

                } catch (error) {
                    console.error('Failed to decode token', error);
                }
            } else {
                console.error('No token found');
            }
        })();
    }, []);

    const handlePageChange = (selectedPage: any) => {
        setCurrentPage(selectedPage);
        setIsNewSearch(false);
    };

    useEffect(() => {
        setInputValue(currentPage + 1);
    }, [currentPage]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = Number(e.target.value);

        if (userListData?.totalPages && value > userListData.totalPages) {
            value = userListData.totalPages;
        } else if (value < 1) {
            value = 1;
        }
        setInputValue(value);

        if (debounceTimer) clearTimeout(debounceTimer);

        const timer = setTimeout(() => {
            if (userListData?.totalPages && value >= 1 && value <= userListData?.totalPages) {
                handlePageChange(value - 1);
            }
        }, 500);

        setDebounceTimer(timer);
    };

    useEffect(() => {
        const usersList = async () => {
            let response;
            const activeSearch = searchName
                ? {field: "accountName", keyword: searchName}
                : searchWorkingPoint
                    ? {field: "workingPointName", keyword: searchWorkingPoint}
                    : null;
            const requestBody: any = {
                advancedSearch: {
                    fields: [activeSearch?.field],
                    keyword: activeSearch?.keyword
                },
                pageNumber: currentPage + 1,
                pageSize: pageSize
            };

            const requestBodySimple: any = {
                keyword: "",
                pageNumber: currentPage + 1,
                pageSize: pageSize
            };

            if (activeSearch) {
                if (isNewSearch) setCurrentPage(0);

                response = await ApiHttpClient().post<PaginatedUsersResponse>(`/users/userManagement/list`, requestBody);
            } else {
                response = await ApiHttpClient().post<PaginatedUsersResponse>(`/users/userManagement/list`, requestBodySimple);
            }
            

            if (!response?.data) {
                toast({description: "A aparut o eroare pentru afisarea utilizatorilor"});
                return <></>;
            }
            setUserListData(response.data);
            setIsNewSearch(false);
        }
        usersList();
    }, [currentPage, pageSize, searchName, searchWorkingPoint, isNewSearch, usersRefreshKey]);
    
    const handleResetPassword = (name: string) => {
       setConfirmResetPassword(true);
       setUserName(name);
    }

    const handleDeactivateUser = (id: number, name: string) => {
        setUserIdToDeactivate(id);
        setUserToDeactivate(name);
        setConfirmDeactivateUser(true);
    }

    const closeDeactivateModal = () => {
        if (isDeactivating) return;
        setConfirmDeactivateUser(false);
        setUserToDeactivate("");
        setUserIdToDeactivate(null);
    }

    const handleConfirmDeactivate = async () => {
        if (userIdToDeactivate == null) return;

        setIsDeactivating(true);
        try {
            const apiBase = process.env.REACT_APP_AIS_ENDPOINT?.replace(/\/v1\/?$/, '') ?? '';
            await ApiHttpClient().delete(`${apiBase}/users?request=${userIdToDeactivate}`);

            toast({ description: `Utilizatorul ${userToDeactivate} a fost dezactivat.` });
            setConfirmDeactivateUser(false);
            setUserToDeactivate("");
            setUserIdToDeactivate(null);
            setUsersRefreshKey((value) => value + 1);
        } catch (error: any) {
            const errors = error.response?.data?.errors;
            const message = Array.isArray(errors) && errors.length > 0
                ? errors.join(" ")
                : error.response?.data?.title || "Eroare la dezactivarea utilizatorului.";
            toast({ description: message });
        } finally {
            setIsDeactivating(false);
        }
    }

    useEffect(() => {
        isDeactivatingRef.current = isDeactivating;
    }, [isDeactivating]);

    useEffect(() => {
        if (!confirmDeactivateUser) return;

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape" && !isDeactivatingRef.current) {
                setConfirmDeactivateUser(false);
                setUserToDeactivate("");
                setUserIdToDeactivate(null);
            }
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [confirmDeactivateUser]);

    const refreshPage = () => {
        setConfirmResetPassword(false);
    }
    
    const handleResetPasswordForUser = async () => {
        if (!newPassword) {
            toast({ description: "Parola nu poate fi goală." });
            return;
        }

        setIsSaving(true);
        try {
            const apiBase = process.env.REACT_APP_AIS_ENDPOINT?.replace(/\/v1\/?$/, '') ?? '';

            const payload = {
                accountName: userName,
                password: newPassword,
            };

            await ApiHttpClient().post(`${apiBase}/users/resetPassword`, payload);

            setPasswordErrors([]);
            toast({ description: `Parola pentru ${userName} a fost resetată cu succes.` });
            refreshPage();
        } catch (error: any) {
            if (error.response?.data?.errors?.length > 0) {
                setPasswordErrors(error.response.data.errors);
            } else {
                toast({ description: "Eroare la resetarea parolei." });
            }
        } finally {
            setIsSaving(false);
        }
    }
    
    return (
        <>
            <Navbar />
            <div className="flex flex-row items-start">
                <SideMenu />
                <div className="flex flex-col overflow-auto w-full">
                    <div className="text-4xl px-8 py-3 mt-5">Administrare Utilizatori Franciza</div>
                     <div className="overflow-x-auto px-8 py-2 mt-2">

                       {canAccessCreateNewUser && <a href={"/user-management/new-user/"}>
                            <button
                                type="button"
                                className="inline-block w-4/5 rounded-lg bg-red-500 px-3 py-2 mb-5 font-medium text-white sm:w-auto"
                            >
                                Creeaza un utilizator nou
                            </button>
                        </a>}

                         {!confirmResetPassword && (
                             <div className="flex flex-col gap-3 sm:flex-row">
                                 <SearchField
                                     value={nameInput}
                                     onValueChange={setNameInput}
                                     setKeyword={setSearchName}
                                     setIsNewSearch={setIsNewSearch}
                                     disabled={workingPointInput.length > 0}
                                     placeholder="Cauta numele unui utilizator..."
                                 />
                                 <SearchField
                                     value={workingPointInput}
                                     onValueChange={setWorkingPointInput}
                                     setKeyword={setSearchWorkingPoint}
                                     setIsNewSearch={setIsNewSearch}
                                     disabled={nameInput.length > 0}
                                     placeholder="Cauta punctul de lucru..."
                                 />
                             </div>
                         )}
                         
                         <table className="min-w-full divide-y-2 divide-gray-200 bg-white text-sm mt-4">
                            {!confirmResetPassword && <thead className="text-left">
                            <tr>
                                <th className="whitespace-nowrap px-4 py-2 font-medium text-gray-900">User Email</th>
                                <th className="whitespace-nowrap px-4 py-2 font-medium text-gray-900">Role</th>
                                <th className="whitespace-nowrap px-4 py-2 font-medium text-gray-900">Client Organisation</th>
                                <th className="whitespace-nowrap px-4 py-2 font-medium text-gray-900">Client Working Point</th>
                                <th className="whitespace-nowrap px-4 py-2 font-medium text-gray-900">Admin UC</th>
                                <th className="whitespace-nowrap px-4 py-2 font-medium text-gray-900 text-center">Actions</th>
                            </tr>
                            </thead>}

                            {!confirmResetPassword && <TableBody>
                                {userListData && userListData.items.length > 0 && userListData?.items.map((user: UsersDetailsModel, index: number) => {
                                    return (
                                        <TableRow key={user.userDetails.id}
                                                  className={index % 2 === 0 ? "bg-slate-150" : "bg-slate-100"}>
                                            <TableCell
                                                className="text-left whitespace-nowrap px-4 py-2 font-medium text-gray-900">{user.userDetails.accountName}</TableCell>
                                            <TableCell className=" whitespace-nowrap px-4 py-2 text-gray-700">
                                                {user.userDetails.roles[0].roleName}
                                            </TableCell>
                                            <TableCell className=" whitespace-nowrap px-4 py-2 text-gray-700">
                                                {user.workingPoint ? user.workingPoint.organisation.organisationName : '-'}
                                            </TableCell>
                                            <TableCell className=" whitespace-nowrap px-4 py-2 text-gray-700">
                                                {user.workingPoint ? user.workingPoint.workingPointName : '-'}
                                            </TableCell>
                                            <TableCell
                                                className=" whitespace-nowrap px-4 py-2 text-gray-700">
                                                {user.userDetails.claims[0]?.key ? ((user.userDetails.claims[0]?.key as string) === "ADMINORG" ? user.userDetails.claims[0].value[0] : '-') : '-'}
                                            </TableCell>

                                            <TableCell
                                                className=" whitespace-nowrap px-4 py-2 text-gray-700">
                                                <div className="flex flex-col gap-2 my-1 min-w-[11.5rem]">
                                                    {canAccessModifyUser && <a href={"/user-management/change-user/" + user.userDetails.id} className="block">
                                                        <button
                                                            type="button"
                                                            className="inline-flex w-full items-center justify-center rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 shadow-sm transition hover:border-blue-300 hover:bg-blue-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                                                        >Modificare utilizator
                                                        </button>
                                                    </a>}
                                                    {canAccessChangePassword && <button
                                                        type="button"
                                                        onClick={() => handleResetPassword(user.userDetails.accountName)}
                                                        className="inline-flex w-full items-center justify-center rounded-lg border border-orange-200 bg-orange-50 px-3 py-1.5 text-sm font-medium text-orange-700 shadow-sm transition hover:border-orange-300 hover:bg-orange-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-200"
                                                    >Reseteaza parola
                                                    </button>}
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDeactivateUser(user.userDetails.id, user.userDetails.accountName)}
                                                        className="inline-flex w-full items-center justify-center rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 shadow-sm transition hover:border-red-300 hover:bg-red-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-200"
                                                    >Dezactiveaza utilizator
                                                    </button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                            }
                        </table>
                        {confirmResetPassword && <article
                            className="rounded-xl border-2 border-gray-100 mx-auto mt-4 bg-white w-1/3 justify-center items-center">
                            <div className="flex flex-col gap-4 p-4 sm:p-6 lg:p-8">
                                <div className="block shrink-0">
                                    Mesaj de avertizare
                                </div>

                                <div>
                                    <h3 className="font-medium sm:text-lg">
                                        Esti sigur ca vrei sa schimbi parola pentru userul <span
                                        className="text-red-500">{userName}</span>?
                                    </h3>

                                    <div className="mt-2">
                                        <label htmlFor="resetPassword"
                                               className="flex justify-between items-center w-full">
                                            <input
                                                type="password"
                                                id="resetPassword"
                                                placeholder="Introduceti noua parola"
                                                value={newPassword}
                                                onChange={(e) => {
                                                    setNewPassword(e.target.value);
                                                    setPasswordErrors([]);
                                                }}
                                                className="bg-gray-100 rounded py-2 px-6 border-gray-300 border-2 shadow-sm sm:text-sm pl-2"
                                            />
                                        </label>
                                    </div>

                                    <div className="flex flex-row space-x-4 mt-3">

                                        <button
                                            className="font-medium text-blue-400 px-3 py-2 bg-blue-50 rounded-lg my-auto"
                                            onClick={refreshPage}>Cancel
                                        </button>

                                        <div className="flex justify-end mx-auto space-x-3 items-center">
                                            <button
                                                className="font-medium text-red-400 px-3 py-2 bg-red-50 rounded-lg my-auto"
                                                onClick={handleResetPasswordForUser}
                                                disabled={isSaving}>
                                                {isSaving ? "Se salvează..." : "Salvează"}
                                            </button>
                                        </div>

                                    </div>
                                </div>
                            </div>
                            {passwordErrors.length > 0 && (
                                <ul className="text-sm text-red-600 list-disc list-inside px-4 mt-5 mb-5">
                                    {passwordErrors.map((err, index) => (
                                        <li key={index}>{err}</li>
                                    ))}
                                </ul>
                            )}
                        </article>
                        }
                    </div>
                    {!confirmResetPassword && <div className="text-center mt-8 mb-2">
                        <ul className="flex justify-center gap-1 text-gray-900 mt-3">
                            <li>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (currentPage > 0) handlePageChange(currentPage - 1);
                                    }}
                                    className="grid mr-2 size-8 place-content-center rounded border border-gray-200 transition-colors hover:bg-gray-50 rtl:rotate-180"
                                    aria-label="Previous page"
                                    disabled={currentPage === 0}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="size-4"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </button>
                            </li>

                            <li>
                                <label htmlFor="Page" className="flex items-center gap-1">
                                    <span className="sr-only">Pagina</span>
                                    <input
                                        type="number"
                                        id="Page"
                                        value={inputValue}
                                        min={1}
                                        max={userListData?.totalPages}
                                        onChange={handleInputChange}
                                        className="h-8 w-10 rounded border-gray-300 text-center sm:text-sm"
                                    />
                                    <span className="text-sm">/ {userListData?.totalPages}</span>
                                </label>
                            </li>

                            <li>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (userListData && currentPage < userListData?.totalPages - 1) handlePageChange(currentPage + 1);
                                    }}
                                    className="grid ml-2 size-8 place-content-center rounded border border-gray-200 transition-colors hover:bg-gray-50 rtl:rotate-180"
                                    aria-label="Next page"
                                    disabled={userListData && currentPage === userListData?.totalPages - 1}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="size-4"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </button>
                            </li>
                        </ul>
                    </div>}
                </div>
            </div>
            {confirmDeactivateUser && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
                    onClick={closeDeactivateModal}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="deactivate-user-title"
                        className="w-full max-w-md rounded-2xl border border-gray-100 bg-white p-6 shadow-xl"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <h2 id="deactivate-user-title" className="text-lg font-semibold text-gray-900">
                            Dezactivare utilizator
                        </h2>
                        <p className="mt-3 text-sm leading-6 text-gray-600">
                            Esti sigur ca vrei sa stergi utilizatorul{" "}
                            <span className="font-semibold text-gray-900">{userToDeactivate}</span>?
                            Dupa aceasta actiune, utilizatorul nu se va mai putea loga in aplicatie.
                        </p>
                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={closeDeactivateModal}
                                disabled={isDeactivating}
                                className="inline-flex items-center justify-center rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-200 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                Anuleaza
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmDeactivate}
                                disabled={isDeactivating}
                                className="inline-flex items-center justify-center rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 shadow-sm transition hover:border-red-300 hover:bg-red-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-200 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {isDeactivating ? "Se dezactiveaza..." : "Dezactiveaza"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default UserManagement;
